import { createContext, useContext, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { FacebookIcon, InstagramIcon, LineIcon, MessengerIcon, TelegramIcon, TikTokIcon, WhatsAppIcon } from "@/components/admin_dashboard/BrandIcons";
import {
  Activity, Calendar, Clock, Cloud, FileSpreadsheet, Folder, Star, CalendarDays, ChevronDown, ChevronRight, CircleDollarSign, Eye, FileText, Hash, HelpCircle,
  BarChart3, BookOpen, Inbox, Workflow, Info, LayoutGrid, Link2, List, Mail, MessageSquare, MoreHorizontal, PanelLeft, Phone, Plus, RefreshCw, Search, Send, Settings2, ShieldCheck,
  SlidersHorizontal, Sparkles, StickyNote, Trash2, Upload, Users, } from "lucide-react";

const DESIGN_WIDTH = 1100;

/** Optional overrides for the hero: a wider design canvas (so content reads smaller) and the admin icon rail. */
export const StageOptions = createContext<{ width: number; rail?: number } | null>(null);

const railIcons = [Inbox, Users, BarChart3, Workflow, BookOpen];

function Rail({ active }: { active: number }) {
  return (
    <nav className="am-rail" aria-hidden>
      <div className="am-rail-top"><img src="/dibs_assets/red_logo_with_transparent_bg.png" alt="" /></div>
      {railIcons.map((Icon, index) => <span key={index} className={index === active ? "is-active" : undefined}><Icon /></span>)}
    </nav>
  );
}

/** Renders the admin UI at a fixed desktop width and scales it to fit, cropping at the bottom like a screenshot. */
export function Stage({ children }: { children: ReactNode }) {
  const options = useContext(StageOptions);
  const designWidth = options?.width ?? DESIGN_WIDTH;
  const outer = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ scale: 0.6, height: 900 });
  useLayoutEffect(() => {
    const node = outer.current;
    if (!node) return;
    const measure = () => {
      const scale = node.clientWidth / designWidth;
      setBox({ scale, height: node.clientHeight / scale });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [designWidth]);
  const style: CSSProperties = { width: designWidth, height: box.height, transform: `scale(${box.scale})` };
  return (
    <div className="am-stage" ref={outer} role="img" aria-hidden>
      <div className="am" style={style}>
        {options?.rail !== undefined ? <div className="am-layout"><Rail active={options.rail} />{children}</div> : children}
      </div>
    </div>
  );
}

export function Header({ children }: { children: ReactNode }) {
  return (
    <header className="am-header">
      <PanelLeft aria-hidden />
      <span className="am-crumbs">{children}</span>
      <span className="am-help"><HelpCircle aria-hidden /> Help</span>
    </header>
  );
}

const chips: Record<string, string> = { Active: "green", New: "blue", "At risk": "amber", Dormant: "peach", Lapsed: "rose", VIP: "purple" };
const Chip = ({ label }: { label: string }) => <span className={`am-chip am-chip--${chips[label]}`}>{label}</span>;

const channelMarks = { tg: TelegramIcon, ig: InstagramIcon, fb: FacebookIcon, ms: MessengerIcon, ln: LineIcon, wa: WhatsAppIcon, tt: TikTokIcon } as const;

/** The real app logo for a chat channel. */
export function ChannelMark({ ch, size = 14 }: { ch: keyof typeof channelMarks; size?: number }) {
  const Icon = channelMarks[ch];
  return <Icon size={size} className="am-mark" />;
}

const channelIcon: Record<string, ReactNode> = {
  Telegram: <ChannelMark ch="tg" />,
  Instagram: <ChannelMark ch="ig" />,
  Facebook: <ChannelMark ch="fb" />,
  WhatsApp: <ChannelMark ch="wa" />,
};

function Spark({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 64 30" className="am-spark" preserveAspectRatio="none" aria-hidden>
      <path d={d} pathLength={1} fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

const patients: Array<[string, string, string, string, number, string]> = [
  ["Daniel Koh", "WhatsApp", "daniel.koh@example.com", "+6589728824", 1, "$95.00"],
  ["Wei Jie Seah", "WhatsApp", "weijie.seah@example.com", "+6589910742", 1, "$85.00"],
  ["Haziq Hamid", "WhatsApp", "haziq.hamid@example.com", "+6594731724", 1, "$98.00"],
  ["Lucas Evans", "Instagram", "lucas.evans@example.com", "+6585084258", 1, "$98.00"],
  ["Ryan Lau", "WhatsApp", "", "+6599113934", 0, "$0.00"],
  ["Yvonne Tan", "Instagram", "", "+6581975923", 0, "$0.00"],
  ["Natalie Chua", "Instagram", "natalie.chua@example.com", "+6585156130", 1, "$60.00"],
  ["Clarissa Chan", "WhatsApp", "clarissa.chan@example.com", "+6591601853", 1, "$98.00"],
  ["Cheryl Ng", "Instagram", "cheryl.ng@example.com", "+6593352815", 1, "$95.00"],
  ["Desmond Soh", "Instagram", "desmond.soh@example.com", "+6592234246", 1, "$98.00"],
  ["Shawn Soh", "Instagram", "shawn.soh@example.com", "+6580506335", 1, "$95.00"],
  ["Desmond Teo", "Instagram", "desmond.teo@example.com", "+6589608373", 1, "$85.00"],
  ["Jia Hui Ong", "Instagram", "", "+6591001835", 0, "$0.00"],
  ["Jonathan Seah", "WhatsApp", "", "+6592573188", 0, "$0.00"],
  ["Kenneth Lim", "Instagram", "kenneth.lim@example.com", "+6580039861", 1, "$95.00"],
  ["Kenji Tanaka", "Instagram", "kenji.tanaka@example.com", "+6589059409", 1, "$98.00"],
  ["Joshua Pereira", "Instagram", "joshua.pereira@example.com", "+6592042931", 1, "$98.00"],
  ["Mei Ling Koh", "WhatsApp", "meiling.koh@example.com", "+6591182736", 2, "$190.00"],
  ["Arjun Nair", "Instagram", "arjun.nair@example.com", "+6597721045", 1, "$98.00"],
  ["Sophie Lim", "WhatsApp", "sophie.lim@example.com", "+6586630912", 1, "$95.00"],
  ["Farhan Yusof", "Instagram", "", "+6598814420", 0, "$0.00"],
  ["Grace Tay", "WhatsApp", "grace.tay@example.com", "+6590274581", 3, "$285.00"],
  ["Ivan Chong", "Instagram", "ivan.chong@example.com", "+6583357196", 1, "$85.00"],
  ["Nadia Rahim", "WhatsApp", "nadia.rahim@example.com", "+6594406638", 1, "$98.00"],
  ["Brandon Yeo", "Instagram", "", "+6587745203", 0, "$0.00"],
  ["Tiffany Goh", "WhatsApp", "tiffany.goh@example.com", "+6596618874", 2, "$193.00"],
  ["Ahmad Syafiq", "Instagram", "ahmad.syafiq@example.com", "+6582290417", 1, "$95.00"],
  ["Rebecca Wong", "WhatsApp", "rebecca.wong@example.com", "+6591053329", 1, "$98.00"],
  ["Marcus Heng", "Instagram", "marcus.heng@example.com", "+6599917560", 1, "$85.00"],
];

export function CrmMock() {
  const stats = [
    ["Monthly engaged customers", "1,067", "+12.4%", "M2 24 L18 23 L34 24 L46 18 L54 6 L62 8"],
    ["Inactive customers", "63", "-14.5%", "M2 6 L16 8 L32 14 L46 18 L54 22 L62 24"],
    ["New customers", "102", "+8.3%", "M2 24 L16 22 L28 18 L38 20 L50 6 L62 6"],
    ["Repeat rate", "76.5%", "+4.2%", "M2 26 L16 25 L30 18 L44 12 L54 6 L62 6"],
  ] as const;
  return (
    <Stage>
      <div className="am-main">
        <Header>Patients</Header>
        <div className="am-bar">
          <span className="am-input"><Search aria-hidden /> Search patients...</span>
          <span className="am-spacer" />
          <span className="am-btn am-btn--primary"><Plus aria-hidden /> New patient</span>
          <span className="am-btn"><Upload aria-hidden /> Import patients</span>
        </div>
        <div className="am-content">
          <div className="am-stats">
            {stats.map(([label, value, delta, path]) => (
              <div className="am-stat" key={label}>
                <span><Users aria-hidden />{label}</span>
                <div><b>{value}</b><em className="is-pos">{delta}</em><Spark d={path} /></div>
              </div>
            ))}
          </div>
          <div className="am-filters">
            <span className="am-btn"><Users aria-hidden /> All patients <ChevronDown aria-hidden /></span>
            <span className="am-btn"><SlidersHorizontal aria-hidden /> Filter</span>
            <span className="am-btn am-btn--icon"><Settings2 aria-hidden /></span>
            <span className="am-spacer" />
            <span className="am-seg"><b><List aria-hidden /> List</b><em>Relationships</em></span>
          </div>
          <table className="am-table">
            <thead><tr><th className="am-check"><i /></th><th>Name</th><th>Status</th><th>Preferred channel</th><th>Email</th><th>Phone number</th><th className="am-r">Bookings</th><th>Total order value</th></tr></thead>
            <tbody>
              {patients.map(([name, channel, email, phone, bookings, value], index) => (
                <tr key={name}>
                  <td className="am-check"><i /></td>
                  <td className="am-name">{name}{index === 0 && <u>1</u>}</td>
                  <td><Chip label="Active" /></td>
                  <td><span className="am-channel">{channelIcon[channel]}{channel}</span></td>
                  <td>{email || "—"}</td>
                  <td>{phone}</td>
                  <td className="am-r">{bookings}</td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Stage>
  );
}

const months: Array<[string, Array<[string, string]>]> = [
  ["September 2026", [["3:25 PM, Sep 20, 2026", "about 1 month ago"], ["3:25 PM, Sep 10, 2026", "about 1 month ago"], ["3:25 PM, Sep 3, 2026", "about 2 months ago"]]],
  ["August 2026", [["3:25 PM, Aug 20, 2026", "2 months ago"], ["3:25 PM, Aug 6, 2026", "3 months ago"]]],
];

export function ProfileMock() {
  return (
    <Stage>
      <div className="am-main">
        <Header><span className="am-dim">Customers</span><ChevronRight aria-hidden /><b>Customer profile</b></Header>
        <div className="am-profile">
          <aside>
            <div className="am-person"><i>S</i><div><h4>Samantha Lee</h4><small>CS-5DG8-BBH8</small></div></div>
            <div className="am-actions"><span className="am-btn"><MessageSquare aria-hidden /> Chat with customer</span><span className="am-btn"><Trash2 aria-hidden /> Delete customer</span></div>
            <h5>Synopsis <Sparkles aria-hidden /></h5>
            <p>This is an at risk customer with 2 bookings and a lifetime value of $600. They were last active 32 days ago.</p>
            <small className="am-dim">Generated 1 day ago</small>
            <hr />
            <h5>Recommended next step <Sparkles aria-hidden /></h5>
            <p>P5 Math Weekly ($280/month, Saturday slot) — parent explicitly requested this programme and asked for enrolment details</p>
            <hr />
            <h5>Attributes</h5>
            <dl>
              <div><dt><Activity aria-hidden />Status</dt><dd className="am-amber">At Risk</dd></div>
              <div><dt><Mail aria-hidden />Email</dt><dd>samantha.lee@example.com</dd></div>
              <div><dt><Phone aria-hidden />Phone number</dt><dd>+6596123848</dd></div>
              <div><dt><Hash aria-hidden />Bookings</dt><dd>2</dd></div>
              <div><dt><CircleDollarSign aria-hidden />Total order</dt><dd>$600.00</dd></div>
              <div><dt><Calendar aria-hidden />Date joined</dt><dd>Aug 2, 2026</dd></div>
            </dl>
          </aside>
          <section>
            <div className="am-tabs"><span><ShieldCheck aria-hidden />Actions queue</span><span className="is-active"><Activity aria-hidden />Activities</span><span><Eye aria-hidden />Intel</span></div>
            <div className="am-panel">
              <div className="am-panel-head">
                <div><h4>Activity over time</h4><p>Engagement events over the selected period</p></div>
                <span className="am-seg"><i><CalendarDays aria-hidden /></i><b>All</b><em>1 month</em><em>1 week</em></span>
              </div>
              <div className="am-chartwrap">
                <div className="am-yaxis">{[24, 18, 12, 6, 0].map((n) => <span key={n}>{n}</span>)}</div>
                <svg viewBox="0 0 600 120" className="am-chart" preserveAspectRatio="none" aria-hidden>
                  {[0, 1, 2, 3, 4].map((i) => <line key={i} x1="0" x2="600" y1={4 + i * 28} y2={4 + i * 28} />)}
                  <path pathLength={1} d="M2 18 C5 70 8 116 14 116 C20 116 22 108 28 116 L150 116 C156 116 158 108 164 116 L290 116 C296 116 298 108 304 116 L350 116 C356 116 358 108 364 116 L430 116 C436 116 438 108 444 116 L598 116" />
                </svg>
                <div className="am-xaxis">{["Aug 3", "Aug 14", "Aug 26", "Sep 6", "Sep 17", "Sep 29"].map((d) => <span key={d}>{d}</span>)}</div>
              </div>
            </div>
            <div className="am-panel">
              <h4>Recent activity</h4>
              <p>Latest events from across all channels</p>
              {months.map(([month, events]) => (
                <div key={month} className="am-month">
                  <h5>{month}</h5>
                  <div className="am-events">
                    {events.map(([time, ago]) => (
                      <div className="am-event" key={time}>
                        <i />
                        <div><b><MessageSquare aria-hidden />Message received <WhatsAppIcon size={15} /></b><small>{time}</small></div>
                        <em>{ago}</em>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </Stage>
  );
}

const threads: Array<[string, string, string, string, string]> = [
  ["E", "Evan", "Oct 18", "Hi Evan, thanks for coming today! How was...", "tg"],
  ["SL", "Samantha Lee", "Today", "Hi, it's Samantha. Can Chloe start this S...", "wa"],
  ["SL", "Samantha Lee", "Mon", "Is there a sibling discount?", "ig"],
  ["HT", "Hannah Teo (@oxfordroppout)", "Aug 30", "Hey! 😊 Just wanted to pop by and say hi —...", "ig"],
  ["LC", "Lucas Chua", "Aug 6", "Hi there! Are you looking to book a class for...", "ms"],
  ["OT", "Olivia Tan", "Aug 6", "Hi! This is Priya from Math Vision Enrichmen...", "ln"],
  ["EW", "Ethan Wong", "Aug 2", "Of course. New students can start with a...", "tg"],
  ["NI", "Nur Izzati (+65 9166 3904)", "Aug 1", "For Secondary 2, the weekly Math program...", "ig"],
  ["69", "+65 9354 2856", "Jul 22", "Hey Marcus! Just checking — did you manag...", "wa"],
  ["ML", "Marcus Lim (+65 00000104)", "Jul 18", "Yes — I can help with the JC2 H2 Math...", "wa"],
  ["AR", "Aisha Rahman (+65 9204 6185)", "Jul 11", "The PSLE intensive combines topical master...", "ig"],
  ["DK", "Daniel Koh", "Jul 9", "Yes, there are weekday evening options for ...", "tg"],
  ["ML", "Mei Lin Tan (+65 00000101)", "Jul 5", "Absolutely — the next P5 Math options are...", "wa"],
];

export function InboxMock() {
  return (
    <Stage>
      <div className="am-main">
        <Header>Inbox</Header>
        <div className="am-bar"><span className="am-input"><Search aria-hidden /> Search conversations...</span><span className="am-spacer" /><span className="am-btn"><RefreshCw aria-hidden /> Re-sync</span></div>
        <div className="am-inbox">
          <div className="am-threads">
            {threads.map(([initials, name, date, preview, ch], index) => (
              <div className={`am-thread${index === 1 || index === 2 ? " is-selected" : ""}`} key={`${name}-${ch}`}>
                <span className="am-avatar">{initials}<span className="am-ch-badge"><ChannelMark ch={ch as keyof typeof channelMarks} size={14} /></span></span>
                <div><b>{name}<small>{date}</small></b><span>{preview}</span></div>
              </div>
            ))}
          </div>
          <div className="am-chat">
            <div className="am-chat-head">
              <span className="am-avatar">SL</span>
              <div><b>Samantha Lee</b><small>One customer profile · At risk</small></div>
              <span className="am-apps"><ChannelMark ch="ig" size={16} /><ChannelMark ch="wa" size={16} />2 apps, matched</span>
              <span className="am-ai-pill"><Sparkles aria-hidden /> AI replying</span>
            </div>
            <div className="am-split">
              <section className="am-pane am-pane--ig">
                <header><ChannelMark ch="ig" size={16} /><b>Instagram</b><small>Mon 5 Oct</small></header>
                <div className="am-messages">
                  <p className="am-msg is-in">Hi! Do you have a weekly P5 Math class on Saturdays?</p>
                  <p className="am-msg is-out"><em><Sparkles aria-hidden /> Dibs AI</em>Hi Samantha! Yes, P5 Math runs on Saturdays at 10am for $280/month. The first lesson is a free trial.</p>
                  <p className="am-msg is-in">Is there a sibling discount?</p>
                  <p className="am-msg is-out"><em><Sparkles aria-hidden /> Dibs AI</em>Yes, siblings get 10% off the monthly fee.</p>
                </div>
              </section>
              <section className="am-pane am-pane--wa">
                <header><ChannelMark ch="wa" size={16} /><b>WhatsApp</b><small>Today</small></header>
                <div className="am-messages">
                  <p className="am-msg is-in">Hi, it's Samantha. Can Chloe start this Saturday?</p>
                  <p className="am-msg is-out"><em><Sparkles aria-hidden /> Dibs AI</em>Of course, Samantha! Chloe is in P5, so I've booked her into P5 Math on Saturday at 10am, with <mark>the 10% sibling discount you asked about on Instagram</mark>. I'll remind you the evening before.</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </Stage>
  );
}

const folders: Array<[string, number]> = [["Business info", 6], ["SOPs", 9], ["FAQs", 14], ["Offers and pricing", 5], ["Customer files", 8]];
const library: Array<[string, typeof Folder, number | null]> = [["All data", Folder, 42], ["Favourites", Star, 3], ["Recently updated", Clock, null]];

const knowledge: Array<[string, string, string, string, string, string, string]> = [
  ["Prospect reply scripts.md", "md", "", "", "File", "Ready", "18 Oct 2026"],
  ["Class pricing and packages", "sheet", "", "", "Sheet", "Ready", "16 Oct 2026"],
  ["Cancellation and make-up policy", "note", "", "", "Note", "Ready", "14 Oct 2026"],
  ["New client onboarding SOP.pdf", "pdf", "", "", "File", "Ready", "12 Oct 2026"],
  ["Parent enquiries FAQ", "note", "", "", "Note", "Ready", "9 Oct 2026"],
  ["YAG grant follow-up responses.pdf", "pdf", "Samantha Lee", "S", "File", "Ready", "24 Sept 2026"],
  ["Trial class policy", "note", "Rachel Goh", "R", "Note", "Ready", "24 Sept 2026"],
  ["Tone of voice guide", "note", "", "", "Note", "Ready", "2 Sept 2026"],
  ["Student progress notes.pdf", "pdf", "Nur Izzati", "N", "File", "Ready", "23 Aug 2026"],
  ["About our business", "note", "", "", "Note", "Ready", "29 May 2026"],
  ["Business website", "link", "", "", "Link", "Syncing", "29 May 2026"],
];

const kindIcon = { md: FileText, pdf: FileText, sheet: FileSpreadsheet, note: StickyNote, link: Link2 } as const;

export function KnowledgeMock() {
  return (
    <Stage>
      <div className="am-main">
        <Header>Knowledge</Header>
        <div className="am-ktabs"><span className="is-active">Data</span><span>AI instructions</span></div>
        <div className="am-bar">
          <span className="am-input"><Search aria-hidden /> Search knowledge...</span>
          <span className="am-btn">Last updated</span>
          <span className="am-seg"><b><List aria-hidden /> Table</b><em><LayoutGrid aria-hidden /> Grid</em></span>
          <span className="am-spacer" />
          <span className="am-btn am-btn--primary"><Plus aria-hidden /> Add data</span>
        </div>
        <div className="am-kbody">
          <aside>
            <small>Library</small>
            {library.map(([label, Icon, count], index) => (
              <span key={label} className={index === 0 ? "is-active" : ""}><Icon aria-hidden />{label}{count !== null && <em>{count}</em>}</span>
            ))}
            <small>Folders</small>
            {folders.map(([label, count]) => <span key={label}><Folder aria-hidden />{label}<em>{count}</em></span>)}
            <div className="am-storage"><Cloud aria-hidden /><div><b>42 items synced</b><span>Dibs AI uses all of it</span></div></div>
          </aside>
          <div className="am-ktable">
            <table className="am-table am-table--k">
              <thead><tr><th className="am-check"><i /></th><th className="am-kcol-name">Name</th><th>Associated with</th><th>Type</th><th>Status</th><th>Last updated</th><th className="am-kmenu" /></tr></thead>
              <tbody>
                {knowledge.map(([name, kind, who, initial, type, status, date]) => {
                  const Icon = kindIcon[kind as keyof typeof kindIcon];
                  return (
                    <tr key={name}>
                      <td className="am-check"><i /></td>
                      <td><span className="am-kname"><i className={`am-kicon am-kicon--${kind}`}><Icon aria-hidden /></i>{name}</span></td>
                      <td>{who && <span className="am-assoc"><i>{initial}</i>{who}</span>}</td>
                      <td><span className="am-type">{type}</span></td>
                      <td><span className="am-status"><i className={status === "Ready" ? "is-ready" : "is-sync"} />{status}</span></td>
                      <td className="am-dim">{date}</td>
                      <td className="am-kmenu"><MoreHorizontal aria-hidden /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Stage>
  );
}

function WorkflowCard({ title, text, runs, selected }: { title: string; text: string; runs: number; selected?: boolean }) {
  return (
    <div className={`am-wf${selected ? " is-selected" : ""}`}>
      <div><b>{title}</b><span className="am-switch is-on"><i /></span></div>
      <p>{text}</p>
      <footer><span>{runs} runs this month <ChevronRight aria-hidden /></span><em><i />On</em></footer>
    </div>
  );
}

export function WorkflowMock() {
  return (
    <Stage>
      <div className="am-main">
        <Header>Workflows</Header>
        <div className="am-journey">
          <div className="am-canvas">
            <div className="am-banner"><Info aria-hidden />Dibs personalises each customer's journey using purpose-built workflows. The Orchestration Agent decides which workflow to use and when.</div>
            <span className="am-trigger"><i />New customer</span>
            <div className="am-group" style={{ left: 190 }}>
              <small>Conversion</small>
              <WorkflowCard selected runs={24} title="Convert a new lead" text="Understand new enquiries and keep promising leads moving towards becoming customers." />
              <WorkflowCard runs={11} title="Re-engage a new lead that ghosts" text="Follow up once when a new enquiry goes quiet." />
              <WorkflowCard runs={19} title="Confirm and prepare a booking" text="Send reminders and directions before the first visit." />
            </div>
            <div className="am-group" style={{ left: 480 }}>
              <small>Retention</small>
              <WorkflowCard runs={17} title="Win back a lapsed customer" text="Reach out with a personal offer when a regular goes quiet for 30 days." />
              <WorkflowCard runs={8} title="Celebrate a milestone" text="Say thank you when a customer reaches their tenth visit." />
              <WorkflowCard runs={5} title="Recover from a complaint" text="Escalate to the team and follow up once it is resolved." />
            </div>
            <svg className="am-flow" aria-hidden>
              <defs><marker id="am-arrow" markerUnits="userSpaceOnUse" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto"><path d="M0 0 L10 5 L0 10 z" /></marker></defs>
              {[
                "M160 186 H 205",
                "M315 252 V 279", "M315 412 V 439",
                "M206 226 H 192 Q 184 226 184 234 V 472 Q 184 480 192 480 H 205",
                "M424 346 H 452 Q 460 346 460 338 V 194 Q 460 186 468 186 H 495",
                "M424 506 H 452 Q 460 506 460 498 V 354 Q 460 346 468 346 H 495",
                "M605 252 V 279", "M605 412 V 439",
                "M714 226 H 728 Q 736 226 736 234 V 472 Q 736 480 728 480 H 715",
              ].map((d) => <path key={d} d={d} markerEnd="url(#am-arrow)" />)}
            </svg>
          </div>
          <aside className="am-settings">
            <h6>WORKFLOW SETTINGS</h6>
            <div className="am-settings-body">
              <div className="am-settings-title"><b>Convert a new lead</b><span className="am-switch is-on"><i /></span></div>
              <p>Understand new enquiries and keep promising leads moving towards becoming customers.</p>
              <label>Trigger</label><div className="am-field">A new customer sends a message</div>
              <label>Goal</label><div className="am-field">Book a trial class</div>
              <label>Channels</label>
              <div className="am-tags"><span><ChannelMark ch="wa" size={14} />WhatsApp</span><span><ChannelMark ch="ig" size={14} />Instagram</span><span><ChannelMark ch="tg" size={14} />Telegram</span></div>
              <label>Instructions</label><div className="am-field am-field--area">Greet by name, answer pricing questions from the knowledge base, and offer two trial slots. Hand over to the team if the customer asks for a discount.</div>
              <div className="am-results"><div><b>24</b><span>Runs</span></div><div><b>62%</b><span>Converted</span></div><div><b>3 min</b><span>Avg. reply</span></div></div>
            </div>
          </aside>
        </div>
      </div>
    </Stage>
  );
}
